<template>
  <SheetModal
    class="StoryModal"
    size="VisibleHeader"
    theme="Light"
    @close="$emit('close')"
  >
    <div class="StoryModal__content">
      <nav
        class="StoryModal__nav"
        data-testid="nav"
      >
        <ul class="StoryModal__navList">
          <li
            v-for="(_, i) in screens"
            :key="i"
            class="StoryModal__navItem"
            :aria-label="`Screen ${1 + i}`"
            :aria-selected="screenIndex === i"
          />
        </ul>
      </nav>

      <div
        class="StoryModal__author"
      >
        <ShapeIcon
          class="StoryModal__authorIcon"
          name="Hexagon"
        />

        <div class="StoryModal__authorNameContainer">
          <UIText
            class="StoryModal__authorName"
            size="Medium"
            weight="Medium"
            data-testid="author"
          >
            <Transition>
              <span
                v-if="currentScreen"
                :key="currentScreen.author.name"
              >{{ currentScreen.author.name }}</span>
            </Transition>
          </UIText>

          <UIText
            v-if="currentScreen"
            class="StoryModal__authorTimestamp"
            size="2XSmall"
          >
            AI agent<span class="StoryModal__authorBullet">&bull;</span>
            <Timestamp
              :datetime="currentScreen.timestamp"
            />
          </UIText>
        </div>

        <div class="StoryModal__labelContainer">
          <Transition>
            <UIText
              v-if="currentScreen?.label"
              :key="currentScreen.label"
              data-testid="label"
              class="StoryModal__label"
              tag="div"
              size="2XSmall"
              weight="Medium"
            >
              {{ currentScreen.label }}
            </UIText>
          </Transition>
        </div>
      </div>

      <div
        class="StoryModal__screenContainer"
        :data-direction="direction"
      >
        <div
          class="StoryModal__screenTrack"
          data-testid="screenTrack"
        >
          <Transition>
            <slot />
          </Transition>
        </div>
      </div>

      <div
        class="StoryModal__footer"
      >
        <FormButton
          data-testid="backButton"
          variant="Secondary"
          iconName="ArrowLeft"
          aria-label="Back"
          isRounded
          @click="handleBackClick"
        />

        <FormButton
          data-testid="nextButton"
          isRounded
          @click="handleNextClick"
        >
          <span v-if="screenIndex < screens.length - 1">
            Next
          </span>
          <span v-else>
            Close
          </span>
        </FormButton>
      </div>
    </div>
  </SheetModal>
</template>

<script lang="ts" setup>
import FormButton from "@cosine/components/FormButton.vue";
import ShapeIcon from "@cosine/components/ShapeIcon.vue";
import SheetModal from "@cosine/components/SheetModal.vue";
import { IStoryScreen } from "@cosine/components/StoryModal.types";
import Timestamp from "@cosine/components/Timestamp.vue";
import UIText from "@cosine/components/UIText.vue";
import { computed, ref } from "vue";

const screenIndex = defineModel<number>("screenIndex", {
  default: 0,
});

const emit = defineEmits<{
  close: [],
}>();

const {
  screens,
} = defineProps<{
  screens: Array<IStoryScreen>,
}>();

const direction = ref<"forward" | "backward" | null>(null);

const currentScreen = computed(() => screens.at(screenIndex.value));

function handleBackClick () {
  if (screenIndex.value > 0) {
    direction.value = "backward";
    screenIndex.value--;
  } else {
    emit("close");
  }
}

function handleNextClick () {
  if (screenIndex.value < screens.length - 1) {
    direction.value = "forward";
    screenIndex.value++;
  } else {
    emit("close");
  }
}
</script>

<style lang="scss" scoped>
.StoryModal {
  // TODO: find a more scalable way to do this
  :deep(.SheetModal__closeButton) {
    top: 22px;
    right: 4px;
  }

  :deep(.SheetModal__closeButtonIcon) {
    width: 20px;
    height: 20px;
  }
}

.StoryModal__content {
  display: grid;
  grid: min-content min-content auto min-content / auto;
  min-height: 0;
  padding: 16px var(--layoutMargin);
}

.StoryModal__nav {
  margin-bottom: 16px;
}

.StoryModal__navList {
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
}

.StoryModal__navItem {
  height: 2px;

  border-radius: 4px;
  background-color: var(--colorSwissGrey200);

  transition: 500ms background-color var(--easeInOutCubic);

  &[aria-selected="true"] {
    background-color: var(--colorSwissBlack);
  }
}

.StoryModal__author {
  display: grid;
  grid: auto / min-content minmax(0, auto) min-content;
  gap: 8px;
  align-items: center;
  padding-right: 24px;

  white-space: nowrap;
}

.StoryModal__authorIcon {
  position: relative;
  width: 28px;
  height: 32px;
}

.StoryModal__authorNameContainer {
  position: relative;
  top: -1px;
  display: grid;
}

.StoryModal__authorName {
  position: relative;
  display: grid;
  grid-row: 1 / -1;

  &:deep(span) {
    display: block;
    overflow: hidden;

    text-overflow: ellipsis;

    transition: opacity 500ms var(--easeInOutCubic);

    &.v-leave-active {
      position: absolute;
      inset: 0;
    }

    &.v-enter-from,
    &.v-leave-to {
      opacity: 0;
    }
  }
}

.StoryModal__authorTimestamp {
  color: var(--colorSwissAlphaBlack600);
  line-height: 1;
}

.StoryModal__authorBullet {
  position: relative;
  top: -1px;

  margin: 0 4px;

  font-size: 0.75em;
}

.StoryModal__labelContainer {
  position: relative;
  top: -8px;
}

.StoryModal__label {
  padding: 0 6px;

  border-radius: 9999px;
  background-color: var(--colorSwissGrey100);

  line-height: 20px;
  white-space: nowrap;

  transition: opacity 500ms var(--easeInOutCubic);

  &.v-leave-active {
    position: absolute;
    top: 0;
    right: 0;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.StoryModal__screenContainer {
  --padding: var(--layoutMargin);
  position: relative;

  display: grid;
  margin: 0 calc(-1 * var(--padding));
  overflow: hidden;

  mask: linear-gradient(
    to right,
    transparent 0,
    black var(--padding),
    black calc(100% - var(--padding)),
    transparent 100%,
  );
}

.StoryModal__screenTrack {
  position: absolute;
  inset: 16px var(--padding);
  display: grid;
}

.StoryModal__footer {
  display: grid;
  grid: auto / min-content auto;
  gap: 8px;
}
</style>
